import { alpha } from '@mui/material';

// ========================================
// 🎨 SIMPLIFIED PROFESSIONAL THEME
// Red + Gray + White + Black
// Client-customizable primary color
// ========================================

// ========================================
// PRIMARY THEME COLOR (Client Customizable)
// ========================================
export const PRIMARY_COLOR = '#dc2626';  // Change this for different clients

// ========================================
// STANDARD SEMANTIC COLORS (MUI Standards)
// ========================================
export const SUCCESS_COLOR = '#2e7d32';   // MUI Green-700
export const WARNING_COLOR = '#ed6c02';   // MUI Orange-700
export const ERROR_COLOR = '#d32f2f';     // MUI Red-700
export const INFO_COLOR = '#0288d1';      // MUI Light Blue-700

// ========================================
// GRAYSCALE PALETTE
// ========================================
export const BLACK = '#000000';
export const GRAY_900 = '#212121';
export const GRAY_800 = '#424242';
export const GRAY_700 = '#616161';
export const GRAY_600 = '#757575';
export const GRAY_500 = '#9e9e9e';
export const GRAY_400 = '#bdbdbd';
export const GRAY_300 = '#e0e0e0';
export const GRAY_200 = '#eeeeee';
export const GRAY_100 = '#f5f5f5';
export const GRAY_50 = '#fafafa';
export const WHITE = '#ffffff';

// ========================================
// SIDEBAR COLORS
// ========================================
export const SIDEBAR_BG = WHITE;
export const SIDEBAR_TEXT = GRAY_900;
export const SIDEBAR_ICON = GRAY_700;
export const SIDEBAR_HOVER = GRAY_100;
export const SIDEBAR_ACTIVE_BG = alpha(PRIMARY_COLOR, 0.08);
export const SIDEBAR_ACTIVE_TEXT = PRIMARY_COLOR;
export const SIDEBAR_BORDER = GRAY_300;

// ========================================
// TOPBAR COLORS
// ========================================
export const TOPBAR_BG = WHITE;
export const TOPBAR_TEXT = GRAY_900;
export const TOPBAR_ICON = GRAY_700;
export const TOPBAR_HOVER = GRAY_100;
export const TOPBAR_BORDER = GRAY_300;

// ========================================
// BACKGROUND & CARDS
// ========================================
export const PAGE_BG = GRAY_50;
export const CARD_BG = WHITE;
export const CARD_HOVER = GRAY_100;
export const DASHBOARD_BG = GRAY_50;  // ✅ NEW - Same as PAGE_BG

// ========================================
// BORDERS & DIVIDERS
// ========================================
export const BORDER_COLOR = GRAY_300;
export const DIVIDER_COLOR = GRAY_300;

// ========================================
// TEXT COLORS
// ========================================
export const TEXT_PRIMARY = GRAY_900;
export const TEXT_SECONDARY = GRAY_700;
export const TEXT_DISABLED = GRAY_500;
export const TEXT_HINT = GRAY_600;

// ========================================
// BUTTON COLORS
// ========================================
export const BUTTON_PRIMARY_BG = PRIMARY_COLOR;
export const BUTTON_PRIMARY_TEXT = WHITE;
export const BUTTON_PRIMARY_HOVER = alpha(PRIMARY_COLOR, 0.9);

export const BUTTON_SECONDARY_BG = GRAY_200;
export const BUTTON_SECONDARY_TEXT = GRAY_900;
export const BUTTON_SECONDARY_HOVER = GRAY_300;

export const BUTTON_OUTLINED_BORDER = GRAY_400;
export const BUTTON_OUTLINED_TEXT = GRAY_900;
export const BUTTON_OUTLINED_HOVER = GRAY_100;

// ========================================
// STATUS BACKGROUNDS (NEW NAMING CONVENTION)
// ========================================
export const SUCCESS_BG = alpha(SUCCESS_COLOR, 0.1);
export const SUCCESS_TEXT = SUCCESS_COLOR;
export const STATUS_SUCCESS_BG = SUCCESS_BG;       // ✅ NEW - Alias for backward compatibility
export const STATUS_SUCCESS_TEXT = SUCCESS_COLOR;  // ✅ NEW - Alias for backward compatibility

export const WARNING_BG = alpha(WARNING_COLOR, 0.1);
export const WARNING_TEXT = WARNING_COLOR;
export const STATUS_WARNING_BG = WARNING_BG;       // ✅ NEW - Alias for backward compatibility
export const STATUS_WARNING_TEXT = WARNING_COLOR;  // ✅ NEW - Alias for backward compatibility

export const ERROR_BG = alpha(ERROR_COLOR, 0.1);
export const ERROR_TEXT = ERROR_COLOR;
export const STATUS_ERROR_BG = ERROR_BG;          // ✅ NEW - Alias for backward compatibility
export const STATUS_ERROR_TEXT = ERROR_COLOR;     // ✅ NEW - Alias for backward compatibility

export const INFO_BG = alpha(INFO_COLOR, 0.1);
export const INFO_TEXT = INFO_COLOR;
export const STATUS_INFO_BG = INFO_BG;            // ✅ NEW - Alias for backward compatibility
export const STATUS_INFO_TEXT = INFO_COLOR;       // ✅ NEW - Alias for backward compatibility

// ========================================
// ACTION COLORS (Green, Yellow, Blue)
// ========================================
// Green Actions
export const ACTION_GREEN_COLOR = '#16a34a';      // ✅ NEW - Green-600
export const ACTION_GREEN_BG = alpha('#16a34a', 0.1);
export const ACTION_GREEN_TEXT = '#16a34a';
export const ACTION_GREEN_HOVER = alpha('#16a34a', 0.15);

// Yellow Actions (Warning variations)
export const ACTION_YELLOW_COLOR = '#f59e0b';     // ✅ NEW - Amber-500
export const ACTION_YELLOW_BG = alpha('#f59e0b', 0.1);
export const ACTION_YELLOW_TEXT = '#f59e0b';
export const ACTION_YELLOW_HOVER = alpha('#f59e0b', 0.15);

// Blue Actions (Info variations)
export const ACTION_BLUE_COLOR = '#0284c7';       // ✅ NEW - Sky-600
export const ACTION_BLUE_BG = alpha('#0284c7', 0.1);
export const ACTION_BLUE_TEXT = '#0284c7';
export const ACTION_BLUE_HOVER = alpha('#0284c7', 0.15);

// ========================================
// PROGRESS COLORS
// ========================================
export const PROGRESS_BG = GRAY_200;              // ✅ NEW - Progress bar background

// ========================================
// ACHIEVEMENT GRADIENTS (Dashboard)
// ========================================
export const ACHIEVEMENT_GRADIENT_1 = `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${alpha(PRIMARY_COLOR, 0.7)} 100%)`;
export const ACHIEVEMENT_GRADIENT_2 = `linear-gradient(135deg, ${SUCCESS_COLOR} 0%, ${alpha(SUCCESS_COLOR, 0.7)} 100%)`;
export const ACHIEVEMENT_GRADIENT_3 = `linear-gradient(135deg, ${INFO_COLOR} 0%, ${alpha(INFO_COLOR, 0.7)} 100%)`;

// ========================================
// SHADOWS (Material Design 3)
// ========================================
export const SHADOW_SM = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
export const SHADOW_MD = '0 2px 4px 0 rgba(0, 0, 0, 0.08)';
export const SHADOW_LG = '0 4px 8px 0 rgba(0, 0, 0, 0.12)';
export const SHADOW_XL = '0 8px 16px 0 rgba(0, 0, 0, 0.15)';
export const CARD_SHADOW = SHADOW_SM;

// ========================================
// UTILITY FUNCTIONS
// ========================================
export const getStatusColor = (status) => {
  const statusMap = {
    success: SUCCESS_COLOR,
    completed: SUCCESS_COLOR,
    approved: SUCCESS_COLOR,
    warning: WARNING_COLOR,
    pending: WARNING_COLOR,
    review: WARNING_COLOR,
    error: ERROR_COLOR,
    failed: ERROR_COLOR,
    rejected: ERROR_COLOR,
    info: INFO_COLOR,
    processing: INFO_COLOR,
    active: PRIMARY_COLOR,
    default: TEXT_SECONDARY,
  };
  return statusMap[status?.toLowerCase()] || TEXT_SECONDARY;
};

export const getStatusBgColor = (status) => {
  const statusMap = {
    success: SUCCESS_BG,
    completed: SUCCESS_BG,
    approved: SUCCESS_BG,
    warning: WARNING_BG,
    pending: WARNING_BG,
    review: WARNING_BG,
    error: ERROR_BG,
    failed: ERROR_BG,
    rejected: ERROR_BG,
    info: INFO_BG,
    processing: INFO_BG,
    active: alpha(PRIMARY_COLOR, 0.1),
    default: GRAY_100,
  };
  return statusMap[status?.toLowerCase()] || GRAY_100;
};

export const getProgressColor = (percentage) => {
  const p = Number(percentage) || 0;
  if (p >= 100) return SUCCESS_COLOR;
  if (p >= 75) return PRIMARY_COLOR;
  if (p >= 50) return WARNING_COLOR;
  return ERROR_COLOR;
};

export const createAlpha = (color, opacity) => alpha(color, opacity);

export const getContrastText = (backgroundColor) => {
  const darkColors = [GRAY_900, GRAY_800, GRAY_700, PRIMARY_COLOR, SUCCESS_COLOR, ERROR_COLOR, INFO_COLOR];
  return darkColors.includes(backgroundColor) ? WHITE : GRAY_900;
};

export const lightenColor = (color, amount = 0.1) => alpha(color, 1 - amount);
export const darkenColor = (color, amount = 0.1) => alpha(BLACK, amount);

// ========================================
// COMMON COLOR PRESETS
// ========================================
export const COLORS = {
  primaryLight: alpha(PRIMARY_COLOR, 0.1),
  primaryMain: PRIMARY_COLOR,
  primaryDark: alpha(PRIMARY_COLOR, 0.9),
  success: SUCCESS_COLOR,
  successBg: SUCCESS_BG,
  warning: WARNING_COLOR,
  warningBg: WARNING_BG,
  error: ERROR_COLOR,
  errorBg: ERROR_BG,
  info: INFO_COLOR,
  infoBg: INFO_BG,
  black: BLACK,
  white: WHITE,
  gray: {
    900: GRAY_900,
    800: GRAY_800,
    700: GRAY_700,
    600: GRAY_600,
    500: GRAY_500,
    400: GRAY_400,
    300: GRAY_300,
    200: GRAY_200,
    100: GRAY_100,
    50: GRAY_50,
  },
};

// ========================================
// CLIENT THEME PRESETS
// ========================================
export const CLIENT_THEMES = {
  red: '#dc2626',
  blue: '#2563eb',
  green: '#16a34a',
  purple: '#7c3aed',
  orange: '#ea580c',
  teal: '#0d9488',
  indigo: '#4f46e5',
  pink: '#ec4899',
  amber: '#f59e0b',
  cyan: '#06b6d4',
};

// ========================================
// THEME EXPORT
// ========================================
export default {
  PRIMARY_COLOR,
  SUCCESS_COLOR,
  WARNING_COLOR,
  ERROR_COLOR,
  INFO_COLOR,
  BLACK,
  WHITE,
  GRAY_900,
  GRAY_800,
  GRAY_700,
  GRAY_600,
  GRAY_500,
  GRAY_400,
  GRAY_300,
  GRAY_200,
  GRAY_100,
  GRAY_50,
  SIDEBAR_BG,
  SIDEBAR_TEXT,
  SIDEBAR_ICON,
  SIDEBAR_HOVER,
  SIDEBAR_ACTIVE_BG,
  SIDEBAR_ACTIVE_TEXT,
  SIDEBAR_BORDER,
  TOPBAR_BG,
  TOPBAR_TEXT,
  TOPBAR_ICON,
  TOPBAR_HOVER,
  TOPBAR_BORDER,
  PAGE_BG,
  CARD_BG,
  CARD_HOVER,
  DASHBOARD_BG,
  BORDER_COLOR,
  DIVIDER_COLOR,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  TEXT_DISABLED,
  TEXT_HINT,
  BUTTON_PRIMARY_BG,
  BUTTON_PRIMARY_TEXT,
  BUTTON_PRIMARY_HOVER,
  BUTTON_SECONDARY_BG,
  BUTTON_SECONDARY_TEXT,
  BUTTON_SECONDARY_HOVER,
  BUTTON_OUTLINED_BORDER,
  BUTTON_OUTLINED_TEXT,
  BUTTON_OUTLINED_HOVER,
  SUCCESS_BG,
  SUCCESS_TEXT,
  STATUS_SUCCESS_BG,
  STATUS_SUCCESS_TEXT,
  WARNING_BG,
  WARNING_TEXT,
  STATUS_WARNING_BG,
  STATUS_WARNING_TEXT,
  ERROR_BG,
  ERROR_TEXT,
  STATUS_ERROR_BG,
  STATUS_ERROR_TEXT,
  INFO_BG,
  INFO_TEXT,
  STATUS_INFO_BG,
  STATUS_INFO_TEXT,
  ACTION_GREEN_COLOR,
  ACTION_GREEN_BG,
  ACTION_GREEN_TEXT,
  ACTION_GREEN_HOVER,
  ACTION_YELLOW_COLOR,
  ACTION_YELLOW_BG,
  ACTION_YELLOW_TEXT,
  ACTION_YELLOW_HOVER,
  ACTION_BLUE_COLOR,
  ACTION_BLUE_BG,
  ACTION_BLUE_TEXT,
  ACTION_BLUE_HOVER,
  PROGRESS_BG,
  ACHIEVEMENT_GRADIENT_1,
  ACHIEVEMENT_GRADIENT_2,
  ACHIEVEMENT_GRADIENT_3,
  SHADOW_SM,
  SHADOW_MD,
  SHADOW_LG,
  SHADOW_XL,
  CARD_SHADOW,
  getStatusColor,
  getStatusBgColor,
  getProgressColor,
  createAlpha,
  getContrastText,
  lightenColor,
  darkenColor,
  COLORS,
  CLIENT_THEMES,
};
